import { useRef } from "react";
import {
  LAYOUT,
  resizeZoneFromCorner,
  snapZoneResize,
  type ResizeCorner,
  type ZoneGeometry,
} from "@board/shared";

type ResizeGesture = {
  pointer: number;
  startX: number;
  startY: number;
  start: ZoneGeometry;
  corner: ResizeCorner;
  moved: boolean;
};

const CORNERS: ResizeCorner[] = ["nw", "ne", "sw", "se"];

export function ZoneResizeHandles({
  label,
  geometry,
  minHeight,
  scale,
  onPreview,
  onCommit,
  onProblem,
}: {
  label: string;
  geometry: ZoneGeometry;
  minHeight: number;
  scale: number;
  onPreview: (geometry: ZoneGeometry | null) => void;
  onCommit: (geometry: ZoneGeometry) => Promise<void>;
  onProblem: () => void;
}) {
  const gesture = useRef<ResizeGesture | null>(null);

  function begin(event: React.PointerEvent<HTMLButtonElement>, corner: ResizeCorner) {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    gesture.current = {
      pointer: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      start: geometry,
      corner,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  }

  function move(event: React.PointerEvent<HTMLButtonElement>) {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    if (Math.hypot(event.clientX - active.startX, event.clientY - active.startY) < 1) return;
    active.moved = true;
    onPreview(resizeZoneFromCorner(
      active.start,
      active.corner,
      (event.clientX - active.startX) / scale,
      (event.clientY - active.startY) / scale,
      minHeight,
    ));
  }

  function finish(event: React.PointerEvent<HTMLButtonElement>) {
    const active = gesture.current;
    if (!active || active.pointer !== event.pointerId) return;
    gesture.current = null;
    if (!active.moved) {
      onPreview(null);
      return;
    }
    const resized = resizeZoneFromCorner(
      active.start,
      active.corner,
      (event.clientX - active.startX) / scale,
      (event.clientY - active.startY) / scale,
      minHeight,
    );
    const next = snapZoneResize(resized, active.corner, minHeight);
    const saving = onCommit(next);
    onPreview(null);
    saving.catch(onProblem);
  }

  function cancel(event: React.PointerEvent<HTMLButtonElement>) {
    if (gesture.current?.pointer !== event.pointerId) return;
    gesture.current = null;
    onPreview(null);
  }

  function keyDown(event: React.KeyboardEvent<HTMLButtonElement>, corner: ResizeCorner) {
    if (event.repeat || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const dx = event.key === "ArrowLeft" ? -LAYOUT.gridSnap : event.key === "ArrowRight" ? LAYOUT.gridSnap : 0;
    const dy = event.key === "ArrowUp" ? -LAYOUT.gridSnap : event.key === "ArrowDown" ? LAYOUT.gridSnap : 0;
    const next = snapZoneResize(resizeZoneFromCorner(geometry, corner, dx, dy, minHeight), corner, minHeight);
    onCommit(next).catch(onProblem);
  }

  return (
    <>
      {CORNERS.map((corner) => (
        <button
          key={corner}
          type="button"
          className={`zone__resize zone__resize--${corner}`}
          aria-label={`Resize ${label} from the ${corner.startsWith("n") ? "top" : "bottom"} ${corner.endsWith("w") ? "left" : "right"} corner`}
          title="Drag to resize height and width"
          onPointerDown={(event) => begin(event, corner)}
          onPointerMove={move}
          onPointerUp={finish}
          onPointerCancel={cancel}
          onKeyDown={(event) => keyDown(event, corner)}
        >
          <span aria-hidden="true" />
        </button>
      ))}
    </>
  );
}
